"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useAuth } from "../../lib/hooks/useAuth";
import { ProfileDropdown } from "../profile/ProfileDropdown";
import { useState, useEffect } from "react";
import { isRealtimeFeedbackPublicEnabled } from "@/lib/config/realtimeFeedbackPublic";
import { NotificationDropdown } from "./NotificationDropdown";

export function Header() {
  const pathname = usePathname();
  const { user } = useAuth();
  const [isInstructor, setIsInstructor] = useState<boolean | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // 스크롤 감지
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 강사 여부 확인
  useEffect(() => {
    const checkInstructorStatus = async () => {
      if (!user?.uid) {
        setIsInstructor(false);
        return;
      }

      try {
        const { getDb } = await import("../../lib/api/firebase-lazy");
        const { doc, getDoc } = await import("firebase/firestore");

        const db = getDb();

        // 1. Users 컬렉션에서 isInstructor 플래그 확인
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const userData = userDoc.data();

        // 2. Instructors 컬렉션에서 강사 프로필 확인
        const instructorDoc = await getDoc(doc(db, "instructors", user.uid));

        const hasInstructorFlag = userData?.isInstructor === true;
        const hasInstructorProfile = instructorDoc.exists();

        console.log("Header - 강사 상태 확인:", {
          userId: user.uid,
          hasInstructorFlag,
          hasInstructorProfile,
          userData,
          instructorExists: instructorDoc.exists()
        });

        // instructors 컬렉션에 문서가 있으면 무조건 강사로 취급
        setIsInstructor(hasInstructorProfile || hasInstructorFlag);
      } catch (error) {
        console.error("강사 인증 확인 실패:", error);
        setIsInstructor(false);
      }
    };

    checkInstructorStatus();
  }, [user?.uid]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-200 safe-top ${
        isScrolled
          ? "bg-[#0c0c10]/95 border-white/[0.08] shadow-sm shadow-black/20"
          : "bg-[#0c0c10]/80 border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-14 md:h-16">
          {/* 로고 */}
          <div className="flex items-center">
            <Link
              href="/"
              className="flex items-center outline-none focus:outline-none"
            >
              <div className="max-h-[32px] md:max-h-[48px] -ml-2 flex items-center">
                <Image
                  src="/logo2W.png"
                  alt="ConnecTone"
                  width={180}
                  height={40}
                  quality={100}
                  priority
                  unoptimized
                  className="max-h-[32px] md:max-h-[48px]"
                  style={{ width: 'auto', height: 'auto' }}
                />
              </div>
            </Link>
          </div>

          {/* 빈 공간 */}
          <div className="flex-1"></div>

          {/* 모바일: 프로필 드롭다운 */}
          <div className="flex items-center gap-1 md:hidden">
            <NotificationDropdown />
            <ProfileDropdown />
          </div>

          {/* 우측 정렬: 네비게이션 + 사용자 메뉴 */}
          <div className="hidden md:flex items-center space-x-8">
            {/* 데스크톱 네비게이션 */}
            <nav className="flex items-center space-x-8">
              {/* AI 음악 분석 */}
              <Link
                href="/music-analysis/analyze"
                className="text-sm text-gray-300 hover:text-white transition-colors"
              >
                AI 음악분석
              </Link>

              <Link
                href="/expert-feedback"
                className={`text-sm transition-colors ${
                  pathname?.startsWith("/expert-feedback")
                    ? "text-white font-medium"
                    : "text-gray-300 hover:text-white"
                }`}
              >
                프로 피드백
              </Link>

              {isRealtimeFeedbackPublicEnabled() ? (
              <Link
                href="/realtime-feedback"
                className={`text-sm transition-colors ${
                  pathname?.startsWith("/realtime-feedback")
                    ? "text-white font-medium"
                    : "text-gray-300 hover:text-white"
                }`}
              >
                실시간 피드백
              </Link>
              ) : null}
            </nav>

            {/* 데스크톱 사용자 메뉴 */}
            <div className="flex items-center space-x-4">
              <NotificationDropdown />
              {user && isInstructor ? (
                <Link
                  href="/instructor/dashboard"
                  className="hidden rounded-lg border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-white/20 sm:inline-flex"
                >
                  프로 스튜디오
                </Link>
              ) : null}
              <ProfileDropdown />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
